import { formatMoney } from '@/lib/money'

export type PosReceiptPayload = {
  storeName: string
  saleReference: string
  createdAt: string
  currency: string
  paymentMethod: string
  subtotal: string
  taxTotal: string
  total: string
  lines: Array<{ name: string; quantity: number; lineTotal: string }>
  staffName?: string
  /** Offline-queued sale — not yet on server */
  pendingSync?: boolean
  /** Last 6 hex chars of idempotency key for staff reference */
  localRefShort?: string
  /** True when server returned idempotent replay */
  replayed?: boolean
}

type Props = {
  data: PosReceiptPayload
  onPrint: () => void
  onClose: () => void
}

/**
 * Printable receipt — 80mm-oriented layout; uses window.print() from parent.
 */
export function PosReceiptModal({ data, onPrint, onClose }: Props) {
  const payLabel = data.paymentMethod === 'cash' ? 'Cash' : 'Transfer'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-border bg-card p-6 shadow-xl">
        <div id="pos-receipt-print-root" className="receipt-print-area text-sm text-foreground">
          <style>{`
            @media print {
              body * { visibility: hidden !important; }
              #pos-receipt-print-root, #pos-receipt-print-root * { visibility: visible !important; }
              #pos-receipt-print-root {
                position: absolute; left: 0; top: 0; width: 80mm;
                padding: 8mm; font-size: 11pt;
              }
              .no-print { display: none !important; }
            }
            .receipt-print-area { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace; }
          `}</style>
          <p className="text-center font-semibold">{data.storeName}</p>
          <p className="mt-2 text-center text-xs text-muted-foreground">In-store sale</p>
          <p className="mt-3 text-xs text-muted-foreground">
            Ref:{' '}
            {data.pendingSync && data.localRefShort
              ? `Pending sync · ${data.localRefShort}`
              : data.saleReference}
          </p>
          {data.pendingSync ? (
            <p className="text-xs text-amber-800 dark:text-amber-200">This sale will sync when you are online.</p>
          ) : null}
          {data.replayed ? (
            <p className="text-xs text-muted-foreground">(Already recorded — replay)</p>
          ) : null}
          <p className="text-xs text-muted-foreground">{new Date(data.createdAt).toLocaleString()}</p>
          <p className="mt-1 text-xs">Payment: {payLabel}</p>
          {data.staffName ? <p className="text-xs text-muted-foreground">Staff: {data.staffName}</p> : null}
          <hr className="my-3 border-border" />
          <ul className="space-y-1">
            {data.lines.map((l, i) => (
              <li key={i} className="flex justify-between gap-2 text-xs">
                <span>
                  {l.quantity}× {l.name}
                </span>
                <span className="tabular-nums">{formatMoney(Number(l.lineTotal), data.currency)}</span>
              </li>
            ))}
          </ul>
          <hr className="my-3 border-border" />
          <div className="flex justify-between text-xs">
            <span>Subtotal</span>
            <span className="tabular-nums">{formatMoney(Number(data.subtotal), data.currency)}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span>Tax</span>
            <span className="tabular-nums">{formatMoney(Number(data.taxTotal), data.currency)}</span>
          </div>
          <div className="mt-2 flex justify-between font-semibold">
            <span>Total</span>
            <span className="tabular-nums">{formatMoney(Number(data.total), data.currency)}</span>
          </div>
          <p className="mt-6 text-center text-[10px] text-muted-foreground">Thank you</p>
        </div>
        <div className="no-print mt-6 flex gap-2">
          <button
            type="button"
            className="inline-flex flex-1 items-center justify-center rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground active:scale-[0.98]"
            onClick={onPrint}
          >
            Print receipt
          </button>
          <button
            type="button"
            className="inline-flex flex-1 items-center justify-center rounded-xl border border-border px-4 py-2.5 text-sm font-medium"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
